/**
 * Utility functions for handling image URLs
 */

/**
 * Constructs a full image URL from a relative or absolute path
 * @param imagePath - The image path (can be relative like "/uploads/..." or already full URL)
 * @returns Full image URL using the correct base domain
 */
export const getImageUrl = (imagePath: string | null | undefined): string | null => {
  if (!imagePath) {
    return null;
  }

  // If it's already a full URL (starts with http), return as-is
  if (imagePath.startsWith('http')) {
    return imagePath;
  }

  // If it's a relative path (starts with /), prepend the base URL
  if (imagePath.startsWith('/')) {
    // Use VITE_SOCKET_URL as the base domain (e.g., https://solofix.nl)
    const baseUrl = import.meta.env.VITE_SOCKET_URL || 'https://dev.solofix.nl';
    return `${baseUrl}${imagePath}`;
  }

  // If it doesn't start with / or http, assume it's a filename and needs /uploads prefix
  const baseUrl = import.meta.env.VITE_SOCKET_URL || 'https://dev.solofix.nl';
  return `${baseUrl}/uploads/${imagePath}`;
};

/**
 * Constructs a thumbnail image URL from a main image path
 * @param imagePath - The main image path
 * @returns Thumbnail image URL or null if not applicable
 */
export const getThumbnailUrl = (imagePath: string | null | undefined): string | null => {
  if (!imagePath) {
    return null;
  }

  const fullUrl = getImageUrl(imagePath);
  if (!fullUrl) {
    return null;
  }

  // Convert main image path to thumbnail path
  // e.g., /uploads/events/image.jpg -> /uploads/events/thumbnails/image_thumb.jpg
  const lastSlashIndex = fullUrl.lastIndexOf('/');
  const lastDotIndex = fullUrl.lastIndexOf('.');
  
  if (lastSlashIndex === -1 || lastDotIndex === -1) {
    return fullUrl; // Return original if can't determine thumbnail path
  }

  const directory = fullUrl.substring(0, lastSlashIndex);
  const filename = fullUrl.substring(lastSlashIndex + 1, lastDotIndex);
  const extension = fullUrl.substring(lastDotIndex);
  
  return `${directory}/thumbnails/${filename}_thumb${extension}`;
};